import { format } from 'date-fns';
import { nanoid } from 'nanoid';

import {
  AVAILABLE_PANELISTS_CAP,
  DEFAULT_RESPONSE_COUNT,
  QUESTION_TYPES,
  SURVEY_TYPES,
  VIDEO_SINGLE_GROUP_RESPONSE_COUNT,
} from '@api/instant_survey';
import { isEUDomain } from '@helpers';
import { formatData } from '@utilities';

import { SURVEY_TEMPORARY_TITLE } from './constants';
/**
 * format available panelists
 * @param  {Object} options.group
 * @param  {Number} options.group.quotaLimit
 * @param  {Number} options.group.validationUsers
 * @return {String}
 */
export const formatAvailablePanelists = ({ quotaLimit, validationUsers }) => {
  const cappedAvailablePanelists = Math.min(AVAILABLE_PANELISTS_CAP, 15 * quotaLimit);
  const displayedPanelistCount = Math.min(validationUsers, cappedAvailablePanelists);

  return `${formatData({ format: '0,0', value: displayedPanelistCount })}${
    validationUsers >= cappedAvailablePanelists ? '+' : ''
  }`;
};

/**
 * generate temporary survey title
 * @param  {Date} date
 * @return {String}
 */
export const generateTemporarySurveyTitle = (date = new Date()) =>
  `${SURVEY_TEMPORARY_TITLE} - ${format(date, 'MMM dd, yyyy')}`;

export const SEQUENTIAL_MONADIC_CONCEPT_VALIDATION_MESSAGES = {
  CONCEPT_NAMES_REQUIRED: 'All concepts in a sequential monadic section must have names.',
  MINIMUM_CONCEPTS_REQUIRED: 'Sequential monadic sections require at least 2 concepts.',
  MINIMUM_SUBQUESTIONS_REQUIRED: 'Sequential monadic sections require at least 1 question.',
};

export const SEQUENTIAL_MONADIC_QUESTIONNAIRE_VALIDATION_MESSAGES = {
  CONCEPT_IMAGES_MATCH_REQUIRED:
    'All concepts in a sequential monadic section must either have images or have no images.',
  CONCEPT_MINIMUM_REQUIRED: 'Each sequential monadic section must have at least 2 concepts.',
  QUESTION_REQUIRED: 'Each sequential monadic section must have at least one question.',
};

const getActiveSequentialMonadicConcepts = (concepts = []) =>
  concepts.filter(({ _delete }) => !_delete);

const getAnswerableSequentialMonadicSubQuestions = (subQuestions = []) =>
  subQuestions.filter(
    ({ _delete, question_type, type }) =>
      !_delete && (type || question_type) !== QUESTION_TYPES.INSTRUCTIONS.value
  );

/**
 * get sequential monadic question cost
 * @param {Object} question
 * @param {Object[]} [question.concepts]
 * @param {Object[]} [question.subQuestions]
 * @returns {Number}
 */
export const getSequentialMonadicQuestionCost = ({ concepts = [], subQuestions = [] } = {}) =>
  getAnswerableSequentialMonadicSubQuestions(subQuestions).length *
  getActiveSequentialMonadicConcepts(concepts).length;

/**
 * validate sequential monadic question
 * @param {Object} question
 * @param {Object[]} [question.concepts]
 * @param {Object[]} [question.subQuestions]
 * @param {String} question.type
 * @returns {?String}
 */
export const validateSequentialMonadicQuestion = ({
  concepts = [],
  subQuestions = [],
  type,
} = {}) => {
  if (type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value) return null;

  const activeConcepts = getActiveSequentialMonadicConcepts(concepts);
  const answerableSubQuestions = getAnswerableSequentialMonadicSubQuestions(subQuestions);
  const allConceptsHaveNames = activeConcepts.every(({ name }) => Boolean(name?.trim()));

  if (activeConcepts.length < 2) {
    return SEQUENTIAL_MONADIC_CONCEPT_VALIDATION_MESSAGES.MINIMUM_CONCEPTS_REQUIRED;
  }

  if (answerableSubQuestions.length < 1) {
    return SEQUENTIAL_MONADIC_CONCEPT_VALIDATION_MESSAGES.MINIMUM_SUBQUESTIONS_REQUIRED;
  }

  if (!allConceptsHaveNames) {
    return SEQUENTIAL_MONADIC_CONCEPT_VALIDATION_MESSAGES.CONCEPT_NAMES_REQUIRED;
  }

  return null;
};

/**
 * Makes sure a monadic section has at least 2 concepts, at least 1 question,
 * all concepts have names, and all concepts either have images or don't have images.
 *
 * @param {Object} question
 * @param {Object[]} [question.concepts]
 * @param {Object[]} [question.subQuestions]
 * @param {String} question.type
 * @returns {?String}
 */
export const getSequentialMonadicQuestionnaireValidationError = ({
  concepts = [],
  subQuestions = [],
  type,
} = {}) => {
  if (type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value) return null;

  const activeConcepts = getActiveSequentialMonadicConcepts(concepts);
  const answerableSubQuestions = getAnswerableSequentialMonadicSubQuestions(subQuestions);

  if (activeConcepts.length < 2) {
    return SEQUENTIAL_MONADIC_QUESTIONNAIRE_VALIDATION_MESSAGES.CONCEPT_MINIMUM_REQUIRED;
  }

  if (answerableSubQuestions.length === 0) {
    return SEQUENTIAL_MONADIC_QUESTIONNAIRE_VALIDATION_MESSAGES.QUESTION_REQUIRED;
  }

  const allConceptsHaveNames = activeConcepts.every(({ name }) => Boolean(name?.trim()));
  if (!allConceptsHaveNames) {
    return SEQUENTIAL_MONADIC_CONCEPT_VALIDATION_MESSAGES.CONCEPT_NAMES_REQUIRED;
  }

  const conceptImageCount = activeConcepts.filter(({ image }) => Boolean(image)).length;
  if (conceptImageCount > 0 && conceptImageCount < activeConcepts.length) {
    return SEQUENTIAL_MONADIC_QUESTIONNAIRE_VALIDATION_MESSAGES.CONCEPT_IMAGES_MATCH_REQUIRED;
  }

  return null;
};

/**
 * get question cost
 * @param {Object} question
 * @param {Boolean} question._delete
 * @param {Object[]} question.concepts
 * @param {Object} question.properties
 * @param {String} question.type
 * @returns {Number} integer cost to compare against questionnaire pricing
 */
export const getQuestionCost = ({
  _delete,
  concepts,
  isScreener,
  properties,
  subQuestions,
  type,
}) => {
  if (_delete) return 0;
  if (isScreener) return 0;

  switch (type) {
    case QUESTION_TYPES.INSTRUCTIONS.value:
      return 0;
    case QUESTION_TYPES.MAX_DIFF.value:
      return Math.ceil(properties?.numberOfSets / 5);
    case QUESTION_TYPES.SEQUENTIAL_MONADIC.value:
      return getSequentialMonadicQuestionCost({ concepts, subQuestions });
    case QUESTION_TYPES['TABLE-RADIO'].value:
      return Math.ceil(subQuestions?.length / 4);
    default:
      return 1;
  }
};

/**
 * get questions cost - reducer shortcut for get question cost
 * @param {Object[]} questions
 * @returns {Number}
 */
export const getQuestionsCost = (questions = []) =>
  questions.reduce((count, question) => count + getQuestionCost(question), 0);

export const getQuestionsWithScreenersFirst = (questions = []) => {
  const activeScreeners = questions.filter(({ _delete, isScreener }) => !_delete && isScreener);
  const activeStandardQuestions = questions.filter(
    ({ _delete, isScreener }) => !_delete && !isScreener
  );
  const removedQuestions = questions.filter(({ _delete }) => _delete);

  return [...activeScreeners, ...activeStandardQuestions, ...removedQuestions];
};

export const isSequentialMonadicQuestion = (question = {}) =>
  question.type === QUESTION_TYPES.SEQUENTIAL_MONADIC.value;

const isSequentialMonadicSubQuestion = (question = {}) =>
  Boolean(question.sequentialMonadicQuestionId);

const isInstructionQuestion = (question = {}) =>
  (question.type || question.question_type) === QUESTION_TYPES.INSTRUCTIONS.value;

/**
 * Flattens top-level questions with sequential monadic sub-questions for logic lookup.
 * Sequential monadic containers stay in the list as targets, but are never valid sources.
 * @param {Object[]} questions
 * @returns {Object[]}
 */
export const getQuestionLogicQuestions = (questions = []) =>
  questions
    .filter(({ _delete }) => !_delete)
    .flatMap((question, questionIndex) => {
      if (!isSequentialMonadicQuestion(question)) {
        return [{ ...question, questionIndex }];
      }

      const subQuestions = (question.subQuestions || [])
        .filter(({ _delete }) => !_delete)
        .map((subQuestion, subQuestionIndex) => ({
          ...subQuestion,
          questionIndex,
          sequentialMonadicQuestionId: question.id,
          sequentialMonadicSubQuestionIndex: subQuestionIndex,
        }));

      return [
        {
          ...question,
          isSequentialMonadicContainer: true,
          questionIndex,
        },
        ...subQuestions,
      ];
    });

export const findQuestionLogicQuestionById = (questions = [], questionId) =>
  getQuestionLogicQuestions(questions).find(({ id }) => String(id) === String(questionId));

const isGlobalQuestionLogicSource = (question = {}) =>
  !question.isSequentialMonadicContainer &&
  !isSequentialMonadicSubQuestion(question) &&
  !isInstructionQuestion(question);

/**
 * Gets questions that can be used as answer-based logic sources for a target question.
 * @param {Object} options.currentQuestion
 * @param {Object[]} options.questions top-level survey questions
 * @returns {Object[]}
 */
export const getQuestionLogicSourceQuestions = ({ currentQuestion, questions = [] } = {}) => {
  const questionLogicQuestions = getQuestionLogicQuestions(questions);
  const currentQuestionMeta = questionLogicQuestions.find(
    ({ id }) => String(id) === String(currentQuestion?.id)
  );

  if (!currentQuestionMeta) return [];

  return questionLogicQuestions.filter((question) => {
    if (String(question.id) === String(currentQuestionMeta.id)) return false;

    if (isSequentialMonadicSubQuestion(currentQuestionMeta)) {
      if (isSequentialMonadicSubQuestion(question)) {
        return (
          !isInstructionQuestion(question) &&
          question.sequentialMonadicQuestionId ===
            currentQuestionMeta.sequentialMonadicQuestionId &&
          question.sequentialMonadicSubQuestionIndex <
            currentQuestionMeta.sequentialMonadicSubQuestionIndex
        );
      }

      return (
        isGlobalQuestionLogicSource(question) &&
        question.questionIndex < currentQuestionMeta.questionIndex
      );
    }

    return (
      isGlobalQuestionLogicSource(question) &&
      question.questionIndex < currentQuestionMeta.questionIndex
    );
  });
};

export const isValidQuestionLogicSource = ({ currentQuestion, questions, sourceQuestion }) =>
  getQuestionLogicSourceQuestions({ currentQuestion, questions }).some(
    ({ id }) => String(id) === String(sourceQuestion?.id)
  );

/**
 * Count non-instruction questions before a given index
 * Used to determine if a question is beyond Q2 for disqualify logic rules
 * @param {Array} questions - Array of question objects
 * @param {Number} index - Index of the current question
 * @returns {Number} Count of non-instruction questions before this index
 */
export const getNonInstructionQuestionCountBefore = (questions, index) => {
  return questions.slice(0, index).filter(({ type }) => type !== QUESTION_TYPES.INSTRUCTIONS.value)
    .length;
};

/**
 * Calculate sequential starting question numbers for each question, used for numeric comparisons
 * (e.g. reorder logic, skip logic references). Returns raw integers, not display ranges.
 * Does not filter deleted questions — relies on getQuestionCost returning 0 for deleted entries.
 * For UI/DOCX display labels (e.g. "2-4"), use getQuestionDisplayIndexById.
 * @param  {Array} questionsList
 * @return {Object} mapping of question IDs to their starting question number
 */
export const calculateQuestionNumbers = (questionsList) => {
  return questionsList.reduce(
    (acc, question) => {
      const questionCost = getQuestionCost(question);
      acc.questionNumbersByQuestions[question.id] = acc.questionNumber;
      acc.questionNumber += questionCost;
      return acc;
    },
    {
      questionNumber: 1,
      questionNumbersByQuestions: {},
    }
  ).questionNumbersByQuestions;
};

/**
 * Get display index (number or range) for each question
 * @param {Array} questions
 * @returns {Object} mapping of question IDs to display index (e.g., '2-3')
 */
export const getQuestionDisplayIndexById = (questions) => {
  let currentIndex = 1;
  return questions
    .filter(({ _delete }) => !_delete)
    .reduce((acc, question) => {
      if (question.type === QUESTION_TYPES.INSTRUCTIONS.value) {
        acc[question.id] = 'T';
        return acc;
      }
      if (question.isScreener) {
        acc[question.id] = 'S';
        return acc;
      }
      const questionCost = getQuestionCost(question);
      acc[question.id] =
        `${currentIndex}${questionCost > 1 ? `-${currentIndex + questionCost - 1}` : ''}`;
      if (isSequentialMonadicQuestion(question)) {
        let subQuestionIndex = currentIndex;
        (question.subQuestions || [])
          .filter(({ _delete }) => !_delete)
          .forEach((subQuestion) => {
            if (subQuestion.type === QUESTION_TYPES.INSTRUCTIONS.value) {
              acc[subQuestion.id] = 'T';
              return;
            }

            acc[subQuestion.id] = String(subQuestionIndex);
            subQuestionIndex += 1;
          });
      }
      currentIndex += questionCost;
      return acc;
    }, {});
};

/**
 * @typedef MaxDiffQuestionInfo
 * @property {Function} getText
 * @property {Boolean} hasInsufficientCoverage
 * @property {Number} numberOfSets
 * @property {Number} optionsPerSet
 * @property {Number} questionCost
 * @property {Number} timesPerExperiment
 */

/**
 * get Max Diff question information including cost, display counts, and formatted text
 * @param {Question} question
 * @returns {MaxDiffQuestionInfo}
 */
export const getMaxDiffQuestionInfo = (question) => {
  const questionCost = getQuestionCost(question);
  const answersCount = question.answers?.length ?? 0;
  const numberOfSets = question.properties?.numberOfSets;
  const optionsPerSet = question.properties?.optionsPerSet;
  const timesPerExperiment = answersCount
    ? Math.ceil((numberOfSets * optionsPerSet) / answersCount)
    : 0;
  const hasInsufficientCoverage = answersCount > 0 && numberOfSets * optionsPerSet < answersCount;

  return {
    getText: () =>
      `This will count as ${questionCost} question${questionCost !== 1 ? 's' : ''} for your survey. ` +
      `It will be displayed as ${numberOfSets} separate questions; each question will show ` +
      `${optionsPerSet} options; each option will show ${timesPerExperiment} time(s) in the experiment.`,
    hasInsufficientCoverage,
    numberOfSets,
    optionsPerSet,
    questionCost,
    timesPerExperiment,
  };
};

/**
 * get numeric question type descriptive text based on properties
 * @param {Object} properties
 * @returns {String}
 */
export const getNumericTypeText = (properties) => {
  const min = properties?.minNumber ?? '';
  const max = properties?.maxNumber ?? '';

  if (properties?.forceCurrency) {
    const currencySymbol = isEUDomain() ? '€' : '$';
    return `currency write-in between ${currencySymbol}${min} and ${currencySymbol}${max}`;
  }
  if (properties?.forcePercent) {
    return `percentage write-in between ${min}% and ${max}%`;
  }
  return `numeric write-in between ${min} and ${max}`;
};

/**
 * get current questionnaire price
 * @param  {Object[]} options.questionnairePricing
 * @param  {Object} options.survey
 * @return {Object}
 */
export const getCurrentQuestionnairePrice = ({ questionnairePricing, survey }) => {
  const totalCount = getQuestionsCost(survey.questions);
  return questionnairePricing?.find((p) => p.questions === totalCount);
};

/**
 * get current sample price
 * @param  {Object[]} options.samplePricing
 * @param  {Object} options.survey
 * @return {Object}
 */
export const getCurrentSamplePrice = ({ samplePricing, survey }) => {
  const activeGroups = survey.groups.filter(({ _delete }) => !_delete);

  return samplePricing?.find(
    ({ quotaGroups, responses }) =>
      quotaGroups === activeGroups.length &&
      responses === activeGroups.reduce((acc, { quotaLimit }) => acc + quotaLimit, 0)
  );
};

/**
 * get quota limit
 * @param {String} surveyTypeId
 * @return {Number}
 */
export const getQuotaLimit = (surveyTypeId) => {
  if (surveyTypeId === SURVEY_TYPES.VIDEO) return VIDEO_SINGLE_GROUP_RESPONSE_COUNT;
  if (isEUDomain()) return DEFAULT_RESPONSE_COUNT;
  const surveyTypesWithHigherDefault = [
    'standard-v3-instant-survey-type',
    SURVEY_TYPES.MONADIC,
    'concept-test-v3-instant-survey-type',
    SURVEY_TYPES.TURF,
    SURVEY_TYPES.SEQUENTIAL_MONADIC,
    SURVEY_TYPES.NEW_ITEM,
  ];
  return surveyTypesWithHigherDefault.includes(surveyTypeId) ? 300 : DEFAULT_RESPONSE_COUNT;
};

/** get modal config based on survey type
 * @param {String} surveyType
 * @return {Object|null}
 */
export const getModalConfig = ({ surveyType }) => {
  switch (surveyType) {
    case 'monadic':
      return {
        description: `The Monadic Concept Test is used to evaluate multiple concepts—such as product designs,
          claims, or ads—by showing each respondent only one concept. This setup enables clean comparisons
          across distinct groups without bias from seeing multiple options.`,
        link: 'https://help.insights.numerator.com/en/articles/12141196-in-platform-survey-types-about-the-monadic-concept-test-survey',
        steps: [
          'Add concept images or names to each quota group when selecting panelists.',
          'Tag relevant questions to dynamically display each group’s assigned concept.',
          'The final report will compare results by concept, based on quota group exposure.',
        ],
        title: 'About the Monadic Concept Test Survey',
      };

    case 'new-item':
      return {
        description: `The New Item Survey helps you capture shopper reactions to newly released products.
        Use it to measure awareness, early perceptions, and purchase intent shortly after launch.`,
        link: 'https://help.numerator.com/en/articles/13455974-in-platform-survey-types-about-the-new-item-survey',
        steps: [
          'Targets items tagged as “New Items” in our system.',
          'Leverage point forward dates to activate continuously fed sample for up to 90 days.',
          'Follow the Help Center guidance to request manual tagging of specific New Items.',
        ],
        title: 'About the New Item Survey',
      };

    case 'sequential-monadic':
      return {
        description:
          'The Sequential Monadic Concept Test is used to evaluate multiple concepts—such as product designs, claims, or ads—by showing each respondent all concepts one at a time, in a randomized order. This method helps reduce order bias for comparisons across concepts.',
        link: 'https://help.insights.numerator.com/en/articles/12508219-in-platform-survey-types-about-the-sequential-monadic-concept-test',
        steps: [
          'Create question blocks tied to each concept and mark them for randomization.',
          'Respondents will see each concept’s set of questions in a unique, randomized sequence.',
          'The final report will display results by concept, with indicators showing which question blocks were randomized.',
        ],
        title: 'About the Sequential Monadic Concept Test Survey',
      };

    case 'turf':
      return {
        description: `TURF (Total Unduplicated Reach and Frequency) helps identify the optimal combination
        of options—such as features, flavors, or messages—that reaches the largest number of unique respondents.`,
        link: 'https://help.insights.numerator.com/en/articles/12333486-in-platform-survey-types-about-turf',
        steps: [
          'Select your desired panelist criteria.',
          'Add at least one TURF question during questionnaire creation.',
          'The final report will include a TURF analysis showing which combinations maximize reach.',
        ],
        title: 'About the TURF Survey',
      };

    default:
      return null;
  }
};

/**
 * Determines if a survey group is continuously fed based on its date field configuration.
 * A group is considered continuously fed if it has a DATE field with a single value,
 *
 * @param {Object} group
 * @param {Object[]} group.promptAnswers
 * @param {String} group.promptAnswers[].fieldType
 * @param {Array} group.promptAnswers[].values
 * @returns {Boolean}
 *
 **/
export const isContinuouslyFed = (group) => {
  const dateField = group.promptAnswers.find((answer) => answer.fieldType === 'DATE');
  if (!dateField) return false;
  return dateField.values.length === 1 && typeof dateField.values[0] === 'string';
};

/**
 * Checks if question logic is complete.
 * @param {Object} questionLogic - Question logic object.
 * @returns {boolean} - True if complete, false otherwise.
 */
const hasLogicProperty = (row, property) => Object.prototype.hasOwnProperty.call(row, property);

const hasLogicSelections = (selections) => Boolean(selections?.length);

const isQuotaGroupLogicRowComplete = (row) => hasLogicSelections(row.values);

const isDisqualifyLogicRowComplete = (row) => {
  if (!row) return false;

  if (hasLogicProperty(row, 'url_variable')) {
    return isQuotaGroupLogicRowComplete(row);
  }

  return hasLogicSelections(row.answers);
};

const isSkipLogicRowComplete = (row) => {
  if (!row) return false;

  if (hasLogicProperty(row, 'url_variable')) {
    return isQuotaGroupLogicRowComplete(row);
  }

  if (hasLogicProperty(row, 'comparison_value')) {
    return (
      Boolean(row.question?.id) && row.comparison_value !== undefined && row.comparison_value !== ''
    );
  }

  return Boolean(row.question?.id) && hasLogicSelections(row.answers);
};

export const isQuestionLogicComplete = (questionLogic) => {
  if (!questionLogic || Object.keys(questionLogic).length === 0) return true;

  for (const [, logic] of Object.entries(questionLogic)) {
    if (!logic) return false;

    const disqualifyLogic = logic.disqualify;
    const disqualifyLogicRows = disqualifyLogic?.logic;
    if (Array.isArray(disqualifyLogicRows)) {
      for (const row of disqualifyLogicRows) {
        if (!isDisqualifyLogicRowComplete(row)) return false;
      }
    } else if (disqualifyLogic && !hasLogicSelections(disqualifyLogic.answers)) {
      return false;
    }

    const skipLogicRows = logic.skip?.logic;
    if (Array.isArray(skipLogicRows)) {
      for (const row of skipLogicRows) {
        if (!isSkipLogicRowComplete(row)) return false;
      }
    }
  }

  return true;
};

/**
 * Checks whether a question already has a backend id.
 * @param {?Object} question
 * @return {boolean}
 */
const hasPersistedId = (question) => question && question.id !== null && question.id !== undefined;

/**
 * Builds a stable identity string for a question using persisted id, client id, or temp signature.
 * @param {?Object} question
 * @return {string}
 */
const createQuestionKey = (question) => {
  if (!question) return '';
  if (hasPersistedId(question)) return `id:${question.id}`;
  if (question.questionClientId) return `client:${question.questionClientId}`;
  return `temp:${question.text ?? ''}:${question.type ?? ''}:${question._tmpKey ?? ''}`;
};

/**
 * Combines local and server question data, keeping client ids and `_delete` flag when needed.
 * @param {Object} prevQuestion
 * @param {Object} responseQuestion
 * @return {Object}
 */
const mergeQuestionRecord = (prevQuestion, responseQuestion) => {
  const mergedQuestion = {
    ...responseQuestion,
    questionClientId:
      prevQuestion.questionClientId ?? responseQuestion.questionClientId ?? nanoid(),
  };

  if (responseQuestion._delete === undefined && prevQuestion._delete !== undefined) {
    mergedQuestion._delete = prevQuestion._delete;
  }

  return mergedQuestion;
};

/**
 * Ensures every question has a `questionClientId`, reusing previous ids when possible.
 * @param {Object[]} [questions=[]]
 * @param {Object[]} [prevQuestions=[]]
 * @return {Object[]}
 */
export const ensureQuestionClientIds = (questions = [], prevQuestions = []) => {
  if (!Array.isArray(questions) || questions.length === 0) {
    return questions;
  }

  const clientIdByPersistedId = new Map(
    prevQuestions
      .filter((question) => question?.questionClientId && hasPersistedId(question))
      .map((question) => [String(question.id), question.questionClientId])
  );

  return questions.map((question) => {
    if (!question) return question;
    if (question.questionClientId) return question;

    const persistedId = hasPersistedId(question) ? String(question.id) : null;
    if (persistedId && clientIdByPersistedId.has(persistedId)) {
      return { ...question, questionClientId: clientIdByPersistedId.get(persistedId) };
    }

    return { ...question, questionClientId: nanoid() };
  });
};

/**
 * Reconciles questionnaire arrays by overlaying response updates while keeping unsaved locals.
 * @param {Object} params
 * @param {Object[]} [params.prevQuestions=[]]
 * @param {Object[]|undefined} params.responseQuestions
 * @return {Object[]}
 */
export const mergeQuestions = ({ prevQuestions = [], responseQuestions }) => {
  const normalizedPrev = ensureQuestionClientIds(prevQuestions);

  if (responseQuestions === undefined) {
    return normalizedPrev;
  }

  if (!Array.isArray(responseQuestions)) {
    return normalizedPrev;
  }

  const normalizedResponse = ensureQuestionClientIds(responseQuestions, normalizedPrev);
  const responseByKey = new Map();

  normalizedResponse.forEach((question) => {
    responseByKey.set(createQuestionKey(question), question);
  });

  const merged = [];

  normalizedPrev.forEach((prevQuestion) => {
    if (!prevQuestion) return;
    const key = createQuestionKey(prevQuestion);

    if (responseByKey.has(key)) {
      const responseQuestion = responseByKey.get(key);
      responseByKey.delete(key);
      merged.push(mergeQuestionRecord(prevQuestion, responseQuestion));
      return;
    }

    merged.push(prevQuestion);
  });

  normalizedResponse.forEach((question) => {
    const key = createQuestionKey(question);
    if (!responseByKey.has(key)) return;
    responseByKey.delete(key);
    merged.push(question);
  });

  return merged;
};

/**
 * Merges questionLogic maps, keeping logic for still-present questions even if response omitted them.
 * @param {Object} params
 * @param {Object[]} [params.mergedQuestions=[]]
 * @param {Object} [params.prevLogic={}]
 * @param {Object|undefined} [params.responseLogic={}]
 * @return {Object}
 */
export const mergeQuestionLogic = ({
  mergedQuestions = [],
  prevLogic = {},
  responseLogic = {},
}) => {
  if (responseLogic === undefined) return prevLogic;

  const merged = { ...responseLogic };
  const validQuestionIds = new Set(
    mergedQuestions
      .map((question) => question?.id)
      .filter((id) => id !== undefined && id !== null)
      .map((id) => String(id))
  );

  Object.entries(prevLogic || {}).forEach(([questionId, logic]) => {
    if (!validQuestionIds.has(questionId)) return;
    if (!Object.prototype.hasOwnProperty.call(merged, questionId)) {
      merged[questionId] = logic;
    }
  });

  return merged;
};

/**
 * Builds a `questionClientId -> responseQuestion` bridge for unsaved questions using a
 * request-time snapshot. The backend doesn't echo `questionClientId`, so we use:
 *   Pass 1 — positional alignment with a `text + type` guard at each slot (handles the common
 *   case where formatQuestion / transformQuestion preserve order end-to-end).
 *   Pass 2 — `text + type` fallback for snapshot entries Pass 1 missed, gated by uniqueness on
 *   both sides. When two unsaved questions share `text + type` we cannot safely disambiguate
 *   them after a BE reorder, so we deliberately leave them unmatched rather than risk mis-graft.
 * @param {?Object} requestSnapshot
 * @param {Object[]} responseQuestions
 * @return {Map<string, Object>}
 */
const buildQuestionClientIdToResponse = (requestSnapshot, responseQuestions) => {
  const bridge = new Map();
  if (!requestSnapshot || !Array.isArray(requestSnapshot.questions)) return bridge;
  if (!Array.isArray(responseQuestions)) return bridge;

  const snapshotQuestions = requestSnapshot.questions;
  const consumedResponseIndices = new Set();

  // Response slots whose id was already persisted at request-send time are not eligible to be
  // grafted onto unsaved locals — doing so would silently overwrite the existing record on the
  // next save. Excluded from both Pass 1 (positional) and Pass 2 (uniqueness) candidacy.
  const persistedSnapshotQuestionIds = new Set(
    snapshotQuestions
      .filter((snapshot) => snapshot?.id !== null && snapshot?.id !== undefined)
      .map((snapshot) => String(snapshot.id))
  );
  const isPersistedResponseId = (responseQuestion) =>
    persistedSnapshotQuestionIds.has(String(responseQuestion.id));

  const minLength = Math.min(snapshotQuestions.length, responseQuestions.length);
  for (let index = 0; index < minLength; index += 1) {
    const snapshot = snapshotQuestions[index];
    const responseQuestion = responseQuestions[index];
    if (!snapshot?.questionClientId) continue;
    if (snapshot.id !== null && snapshot.id !== undefined) continue;
    if (!responseQuestion) continue;
    if (responseQuestion.id === null || responseQuestion.id === undefined) continue;
    if (isPersistedResponseId(responseQuestion)) continue;
    if ((snapshot.text ?? '') !== (responseQuestion.text ?? '')) continue;
    if ((snapshot.type ?? '') !== (responseQuestion.type ?? '')) continue;
    bridge.set(snapshot.questionClientId, responseQuestion);
    consumedResponseIndices.add(index);
  }

  const buildTextTypeCounts = (items) => {
    const counts = new Map();
    items.forEach((item) => {
      if (!item) return;
      const key = `${item.text ?? ''}::${item.type ?? ''}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  };

  const unmatchedSnapshot = snapshotQuestions.filter(
    (snapshot) =>
      snapshot?.questionClientId &&
      (snapshot.id === null || snapshot.id === undefined) &&
      !bridge.has(snapshot.questionClientId)
  );
  const unmatchedResponse = responseQuestions
    .map((responseQuestion, index) => ({ index, responseQuestion }))
    .filter(
      ({ index, responseQuestion }) =>
        !consumedResponseIndices.has(index) &&
        responseQuestion &&
        responseQuestion.id !== null &&
        responseQuestion.id !== undefined &&
        !isPersistedResponseId(responseQuestion)
    );

  const snapshotCounts = buildTextTypeCounts(unmatchedSnapshot);
  const responseCounts = buildTextTypeCounts(
    unmatchedResponse.map(({ responseQuestion }) => responseQuestion)
  );
  const responseByUniqueKey = new Map();
  unmatchedResponse.forEach(({ responseQuestion }) => {
    const key = `${responseQuestion.text ?? ''}::${responseQuestion.type ?? ''}`;
    if (responseCounts.get(key) === 1) responseByUniqueKey.set(key, responseQuestion);
  });

  unmatchedSnapshot.forEach((snapshot) => {
    const key = `${snapshot.text ?? ''}::${snapshot.type ?? ''}`;
    if (snapshotCounts.get(key) !== 1) return;
    const responseQuestion = responseByUniqueKey.get(key);
    if (!responseQuestion) return;
    bridge.set(snapshot.questionClientId, responseQuestion);
  });

  return bridge;
};

const normalizeGroupName = (name) => name?.trim().toLowerCase() ?? '';

/**
 * Produces a stable string key from a group's promptAnswers for use in bridge signature
 * matching. Keyed on audience-value ids (or JSON for id-less values) so the key is invariant
 * to whether the promptAnswer row itself has been persisted yet.
 */
export const buildPromptAnswersKey = (promptAnswers) => {
  if (!Array.isArray(promptAnswers)) return '';
  return promptAnswers
    .filter((a) => a && !a._delete)
    .map((a) => {
      const valueKey = Array.isArray(a.values)
        ? a.values
            .map((v) =>
              v !== null && typeof v === 'object' && v.id !== null && v.id !== undefined
                ? String(v.id)
                : JSON.stringify(v)
            )
            .sort()
            .join(',')
        : '';
      return `${a.promptId ?? ''}:${a.field ?? ''}:${a.operator ?? ''}:${valueKey}`;
    })
    .sort()
    .join('|');
};

/**
 * Snapshot groups carry a pre-computed `promptAnswersKey`; response groups carry the raw
 * `promptAnswers` array. Both cases are handled so the key is consistent across both sides
 * of the bridge match.
 */
const buildGroupSignatureKey = (group) => {
  const promptKey =
    group?.promptAnswersKey !== undefined
      ? group.promptAnswersKey
      : buildPromptAnswersKey(group?.promptAnswers);
  return [
    normalizeGroupName(group?.name),
    group?.quotaLimit ?? '',
    group?.genRepSampleType ?? '',
    promptKey,
  ].join('::');
};

/**
 * Builds a `clientId -> responseGroup` bridge for unsaved groups using a request-time snapshot.
 * Two-pass match strategy:
 *   Pass 1 — composite signature `(name, quotaLimit, genRepSampleType)`. Only consumes when the
 *   signature is unique on both sides so groups that differ in any captured attribute are paired
 *   deterministically even when names collide.
 *   Pass 2 — `name`-only fallback gated by uniqueness on both sides. When two unsaved groups share
 *   a name and aren't disambiguated by Pass 1, we deliberately leave them unmatched rather than
 *   risk grafting an id onto the wrong row.
 * @param {?Object} requestSnapshot
 * @param {Object[]} responseGroups
 * @return {Map<string, Object>}
 */
const buildClientIdToResponseGroup = (requestSnapshot, responseGroups) => {
  const bridge = new Map();
  if (!requestSnapshot || !Array.isArray(requestSnapshot.groups)) return bridge;
  if (!Array.isArray(responseGroups)) return bridge;

  const persistedSnapshotIds = new Set(
    requestSnapshot.groups
      .filter((group) => group?.id !== null && group?.id !== undefined)
      .map((group) => String(group.id))
  );

  const unsavedSnapshot = requestSnapshot.groups.filter(
    (group) => group?.clientId && (group.id === null || group.id === undefined)
  );
  const unmatchedResponse = responseGroups.filter(
    (responseGroup) =>
      responseGroup &&
      responseGroup.id !== null &&
      responseGroup.id !== undefined &&
      !persistedSnapshotIds.has(String(responseGroup.id))
  );

  const matchUniquePairs = (snapshotGroups, candidateGroups, keyFn) => {
    const snapshotCounts = new Map();
    snapshotGroups.forEach((group) => {
      const key = keyFn(group);
      snapshotCounts.set(key, (snapshotCounts.get(key) || 0) + 1);
    });
    const responseCounts = new Map();
    candidateGroups.forEach((group) => {
      const key = keyFn(group);
      responseCounts.set(key, (responseCounts.get(key) || 0) + 1);
    });
    const responseByKey = new Map();
    candidateGroups.forEach((group) => {
      const key = keyFn(group);
      if (responseCounts.get(key) === 1) responseByKey.set(key, group);
    });
    const consumed = new Set();
    snapshotGroups.forEach((snapshotGroup) => {
      if (bridge.has(snapshotGroup.clientId)) return;
      const key = keyFn(snapshotGroup);
      if (snapshotCounts.get(key) !== 1) return;
      const responseGroup = responseByKey.get(key);
      if (!responseGroup) return;
      bridge.set(snapshotGroup.clientId, responseGroup);
      consumed.add(responseGroup);
    });
    return consumed;
  };

  const consumedBySignature = matchUniquePairs(
    unsavedSnapshot,
    unmatchedResponse,
    buildGroupSignatureKey
  );
  const remainingResponse = unmatchedResponse.filter((group) => !consumedBySignature.has(group));
  matchUniquePairs(unsavedSnapshot, remainingResponse, (group) => normalizeGroupName(group?.name));

  return bridge;
};

/**
 * Picks the response group that should donate ids to a local group. Matches by persisted id first,
 * and falls back to a unique name match for unsaved locals (since a stale response cannot contain
 * the very latest local edits, a value-aware signature would miss those rows).
 * @param {Object} localGroup
 * @param {Object[]} responseGroups
 * @return {?Object}
 */
const pickResponseGroupForIdMerge = (localGroup, responseGroups) => {
  if (!localGroup || !Array.isArray(responseGroups)) return null;

  if (localGroup.id !== null && localGroup.id !== undefined) {
    return (
      responseGroups.find(
        (group) => group?.id !== null && group?.id !== undefined && group.id === localGroup.id
      ) || null
    );
  }

  const localName = localGroup.name?.trim().toLowerCase();
  if (!localName) return null;

  const matches = responseGroups.filter(
    (group) =>
      group?.id !== null &&
      group?.id !== undefined &&
      group.name?.trim().toLowerCase() === localName
  );

  return matches.length === 1 ? matches[0] : null;
};

const hasPersistedRowId = (row) => row && row.id !== null && row.id !== undefined;

/**
 * Drops promptAnswers locally marked `_delete: true` with a persisted id that the response no
 * longer includes. The backend has already honored those deletions, so re-shipping them on the
 * next save would 404 on `InstantSurveyGroupPromptAnswer NNN does not exist`. Rows without a
 * persisted id are kept (the formatter strips them on send anyway) and non-deleted rows pass
 * through unchanged to preserve order and identity.
 * @param {Object[]} localAnswers
 * @param {Object[]} responseAnswers
 * @return {Object[]}
 */
const sweepConfirmedAnswerDeletions = (localAnswers, responseAnswers) => {
  if (!Array.isArray(localAnswers)) return localAnswers;
  const responseIds = new Set(
    (Array.isArray(responseAnswers) ? responseAnswers : [])
      .filter(hasPersistedRowId)
      .map((answer) => String(answer.id))
  );
  return localAnswers.filter((answer) => {
    if (!answer?._delete) return true;
    if (!hasPersistedRowId(answer)) return true;
    return responseIds.has(String(answer.id));
  });
};

/**
 * Drops groups locally marked `_delete: true` with a persisted id that the response no longer
 * includes. Without this sweep, the next autosave would re-ship the delete for a record the
 * backend already removed, which can fail the save and block subsequent quota-group deletions.
 * @param {Object[]} localGroups
 * @param {Object[]} responseGroups
 * @return {Object[]}
 */
const sweepConfirmedGroupDeletions = (localGroups, responseGroups) => {
  if (!Array.isArray(localGroups)) return localGroups;
  const responseIds = new Set(
    (Array.isArray(responseGroups) ? responseGroups : [])
      .filter(hasPersistedRowId)
      .map((group) => String(group.id))
  );
  return localGroups.filter((group) => {
    if (!group?._delete) return true;
    if (!hasPersistedRowId(group)) return true;
    return responseIds.has(String(group.id));
  });
};

/**
 * Returns a clone of `localGroup` with any missing ids copied over from `responseGroup`. Local
 * `values`, `_delete` flags, and other in-progress edits are preserved so newer changes survive.
 * Response answer ids are consumed one-to-one to avoid assigning the same backend row to two
 * local rows in degenerate (multi-row, same prompt/field/operator) cases. Local promptAnswers
 * that were already marked `_delete: true` and have been honored by the backend (no longer in
 * `responseGroup.promptAnswers`) are dropped so the next save can't re-delete a missing record.
 * @param {Object} localGroup
 * @param {Object} responseGroup
 * @param {Object} [options]
 * @param {Boolean} [options.includeValidationFields]
 * @return {Object}
 */
const mergeGroupIdsFromResponse = (localGroup, responseGroup, { includeValidationFields } = {}) => {
  const merged = { ...localGroup };
  if (!hasPersistedRowId(localGroup) && hasPersistedRowId(responseGroup)) {
    merged.id = responseGroup.id;
  }

  if (includeValidationFields) {
    ['validationDatetime', 'validationStatus', 'validationUniqueUsers', 'validationUsers'].forEach(
      (field) => {
        if (Object.prototype.hasOwnProperty.call(responseGroup, field)) {
          merged[field] = responseGroup[field];
        }
      }
    );
  }

  if (!Array.isArray(localGroup.promptAnswers)) {
    return merged;
  }

  const responseAnswers = Array.isArray(responseGroup.promptAnswers)
    ? responseGroup.promptAnswers
    : [];

  const sweptAnswers = sweepConfirmedAnswerDeletions(localGroup.promptAnswers, responseAnswers);

  // Pre-index response answers into FIFO buckets keyed by (promptId, field, operator) so the
  // merge is O(n + m). A head-index per bucket preserves the same first-match order the prior
  // `.find` produced while keeping consumption O(1) — Array.shift would degrade to O(k²) in
  // pathological cases where many response answers share a key.
  const responseAnswerBuckets = new Map();
  responseAnswers.forEach((responseAnswer) => {
    if (!responseAnswer) return;
    if (!hasPersistedRowId(responseAnswer)) return;
    if (responseAnswer._delete) return;
    const key = `${responseAnswer.promptId}::${responseAnswer.field}::${
      responseAnswer.operator ?? ''
    }`;
    if (!responseAnswerBuckets.has(key)) responseAnswerBuckets.set(key, { head: 0, items: [] });
    responseAnswerBuckets.get(key).items.push(responseAnswer);
  });

  merged.promptAnswers = sweptAnswers.map((localAnswer) => {
    if (!localAnswer) return localAnswer;
    if (hasPersistedRowId(localAnswer)) return localAnswer;

    const key = `${localAnswer.promptId}::${localAnswer.field}::${localAnswer.operator ?? ''}`;
    const bucket = responseAnswerBuckets.get(key);
    if (!bucket || bucket.head >= bucket.items.length) return localAnswer;
    const matchingResponseAnswer = bucket.items[bucket.head];
    bucket.head += 1;
    return { ...localAnswer, id: matchingResponseAnswer.id };
  });

  return merged;
};

/**
 * Reconciles local groups with an autosave response on the "fresh" path (no local edits during
 * the in-flight save). Uses local groups as the spine so order, clientIds, and any in-progress
 * UI state survive React reconciliation, then grafts backend ids onto unsaved rows and drops
 * local rows whose deletion the backend has already honored. Newer groups discovered in the
 * response that don't bridge to any local row are appended so the user doesn't lose visibility
 * of server-side additions.
 * @param {Object} params
 * @param {Object[]} [params.prevGroups=[]]
 * @param {?Object} [params.requestSnapshot] see {@link mergeServerIdsFromStaleResponse}
 * @param {Object[]|undefined} params.responseGroups
 * @return {Object[]}
 */
export const mergeGroups = ({ prevGroups = [], requestSnapshot, responseGroups }) => {
  if (!Array.isArray(prevGroups)) return prevGroups;
  if (!Array.isArray(responseGroups)) return prevGroups;

  const clientIdBridge = buildClientIdToResponseGroup(requestSnapshot, responseGroups);
  const sweptGroups = sweepConfirmedGroupDeletions(prevGroups, responseGroups);
  const matchedResponseIds = new Set();

  const merged = sweptGroups.map((localGroup) => {
    if (!localGroup) return localGroup;

    let responseGroup = null;
    if (hasPersistedRowId(localGroup)) {
      responseGroup = pickResponseGroupForIdMerge(localGroup, responseGroups);
    } else if (localGroup.clientId && clientIdBridge.has(localGroup.clientId)) {
      responseGroup = clientIdBridge.get(localGroup.clientId);
    }

    if (!responseGroup) return localGroup;
    matchedResponseIds.add(String(responseGroup.id));
    return mergeGroupIdsFromResponse(localGroup, responseGroup, { includeValidationFields: true });
  });

  responseGroups.forEach((responseGroup) => {
    if (!hasPersistedRowId(responseGroup)) return;
    if (matchedResponseIds.has(String(responseGroup.id))) return;
    merged.push({
      ...responseGroup,
      clientId: responseGroup.clientId ?? nanoid(),
    });
  });

  return merged;
};

/**
 * Reconciles backend-assigned ids from a stale autosave response into the current local survey
 * without clobbering newer in-flight edits. This avoids the "duplicate prompt / duplicate group"
 * problem caused by dropping the response wholesale and then re-sending unsaved rows that the
 * backend then persists a second time.
 * @param {Object} params
 * @param {Object} params.prevSurvey
 * @param {?Object} [params.requestSnapshot] — snapshot of groups and questions captured at
 *   request-send time. `groups` entries carry (clientId, id, name, quotaLimit, genRepSampleType)
 *   and bridge unsaved local groups to response ids via composite signature with name-uniqueness
 *   fallback. `questions` entries carry (questionClientId, id, text, type) and bridge unsaved
 *   local questions to response ids via positional + uniqueness-gated text/type matching. Both
 *   sides exclude response ids that were already persisted at request-send time so the merge
 *   cannot silently overwrite existing records.
 * @param {Object|undefined} params.responseSurvey
 * @return {Object}
 */
export const mergeServerIdsFromStaleResponse = ({
  prevSurvey,
  requestSnapshot,
  responseSurvey,
}) => {
  if (!prevSurvey || !responseSurvey) return prevSurvey;

  const merged = { ...prevSurvey };

  if (
    (prevSurvey.id === null || prevSurvey.id === undefined) &&
    responseSurvey.id !== null &&
    responseSurvey.id !== undefined
  ) {
    merged.id = responseSurvey.id;
  }

  if (Array.isArray(prevSurvey.groups) && Array.isArray(responseSurvey.groups)) {
    const clientIdBridge = buildClientIdToResponseGroup(requestSnapshot, responseSurvey.groups);
    const sweptGroups = sweepConfirmedGroupDeletions(prevSurvey.groups, responseSurvey.groups);

    merged.groups = sweptGroups.map((localGroup) => {
      if (hasPersistedRowId(localGroup)) {
        const responseGroup = pickResponseGroupForIdMerge(localGroup, responseSurvey.groups);
        if (!responseGroup) return localGroup;
        return mergeGroupIdsFromResponse(localGroup, responseGroup);
      }

      if (localGroup?.clientId && clientIdBridge.has(localGroup.clientId)) {
        return mergeGroupIdsFromResponse(localGroup, clientIdBridge.get(localGroup.clientId));
      }

      // With a request snapshot we trust the bridge as the only safe match for unsaved locals;
      // skip the name fallback so a group inserted after request-send can't absorb a stale id.
      if (requestSnapshot) return localGroup;

      const responseGroup = pickResponseGroupForIdMerge(localGroup, responseSurvey.groups);
      if (!responseGroup) return localGroup;
      return mergeGroupIdsFromResponse(localGroup, responseGroup);
    });
  }

  if (Array.isArray(prevSurvey.questions) && Array.isArray(responseSurvey.questions)) {
    const questionBridge = buildQuestionClientIdToResponse(
      requestSnapshot,
      responseSurvey.questions
    );

    merged.questions = prevSurvey.questions.map((localQuestion) => {
      if (!localQuestion) return localQuestion;
      if (hasPersistedId(localQuestion)) return localQuestion;

      if (localQuestion.questionClientId && questionBridge.has(localQuestion.questionClientId)) {
        const responseQuestion = questionBridge.get(localQuestion.questionClientId);
        return { ...localQuestion, id: responseQuestion.id };
      }

      return localQuestion;
    });
  }

  return merged;
};
